import type { ProgressNoteDraftFields } from '@shared/sessions/progress-note'
import {
  SESSION_RECORDING_ASSENT_UNABLE_LABELS,
  SESSION_RECORDING_ERROR_CODES,
  SESSION_RECORDING_NONVERBAL_SIGN_LABELS,
  type SessionRecordingChildAssentMethod,
  type SessionRecordingConsentMethod,
  type SessionRecordingConsenter,
  type SessionRecordingFailure,
  type SessionRecordingPersonRole,
  type SessionRecordingWithdrawnBy,
  type TherapyNoteFlagKind,
} from '@shared/sessions/session-recording'

/** Same labels as the voice-note draft detail (AHP's five progress-note fields). */
export const AHP_FIELD_LABELS: Record<keyof ProgressNoteDraftFields, string> = {
  visitedPlaces: 'Visited places',
  sessionPrep: 'Session prep & research',
  sessionSummary: 'Session summary',
  goalProgress: 'Goal progress',
  smartGoalsComments: 'SMART goals comments',
}

/** Roles a transcript speaker can be named as ("Speaker 1 = Me"). */
export const SPEAKER_NAME_OPTIONS: readonly string[] = [
  'Me',
  'Participant',
  'Parent/guardian',
  'Nominee',
  'Carer',
  'Support worker',
  'Interpreter',
  'Family',
  'Other',
]

export const PERSON_ROLE_LABELS: Record<SessionRecordingPersonRole, string> = {
  participant: 'Participant',
  guardian: 'Parent/guardian',
  nominee: 'Nominee',
  carer: 'Carer',
  support_worker: 'Support worker',
  interpreter: 'Interpreter',
  family: 'Family',
  other: 'Other',
}

/** Roles the provider can add in "Who's here?" (the participant is always listed). */
export const ADDABLE_PERSON_ROLES: readonly Exclude<SessionRecordingPersonRole, 'participant'>[] = [
  'guardian',
  'nominee',
  'carer',
  'support_worker',
  'interpreter',
  'family',
  'other',
]

export const CONSENTER_LABELS: Record<SessionRecordingConsenter, string> = {
  participant: 'The participant',
  guardian: 'Their parent/guardian',
  nominee: 'Their nominee',
  authorised_person: 'Someone who can give consent for them',
}

/** The consent sheet's method choices (provider-facing). */
export const CONSENT_METHOD_OPTIONS: Record<SessionRecordingConsentMethod, { title: string; hint: string }> = {
  participant_tap: {
    title: 'Tap on the Easy Read card',
    hint: 'They tap “Yes, record” — or their guardian or nominee does.',
  },
  verbal_clip: {
    title: 'Record consent now',
    hint: 'Hold the phone near them while they say “I consent to recording”. The short clip is kept as the consent record.',
  },
  nonverbal: {
    title: 'Member indicates consent',
    hint: 'For someone who doesn’t use words: a nod, a gesture, their communication device, or pointing to the card.',
  },
  guardian_app: {
    title: 'Ask in their app',
    hint: 'Send a request to someone who can give consent for them. They answer in their Ascend app.',
  },
}

/** Under 18: how their parent/guardian agrees (in person, or in their app). */
export const GUARDIAN_METHOD_OPTIONS: Record<'verbal_clip' | 'guardian_app', { title: string; hint: string }> = {
  verbal_clip: {
    title: 'Say it out loud, here',
    hint: 'Their parent or guardian says “I consent to recording” on this phone. The short clip is kept as the consent record.',
  },
  guardian_app: {
    title: 'Ask in their app',
    hint: 'Send a request to their parent or guardian. They answer in their Ascend app.',
  },
}

/** How agreement was given, for summaries and the record. */
export const CONSENT_METHOD_SUMMARY: Record<SessionRecordingConsentMethod, string> = {
  participant_tap: 'Tapped “Yes, record” on the Easy Read card',
  verbal_clip: 'Said “I consent to recording” (short clip kept)',
  nonverbal: 'Showed they agree without words',
  guardian_app: 'Agreed in their Ascend app',
}

/** A child's own agreement (provider-facing). */
export const ASSENT_METHOD_LABELS: Record<SessionRecordingChildAssentMethod, string> = {
  participant_tap: 'Taps “Yes, record”',
  verbal_clip: 'Says yes out loud',
  nonverbal: 'Shows yes without words',
  unable: 'Couldn’t show yes or no',
}

export const ASSENT_METHOD_SUMMARY: Record<SessionRecordingChildAssentMethod, string> = {
  participant_tap: 'Tapped “Yes, record”',
  verbal_clip: 'Said yes out loud (short clip kept)',
  nonverbal: 'Showed yes without words',
  unable: 'Couldn’t show yes or no',
}

export const NONVERBAL_SIGN_LABELS = SESSION_RECORDING_NONVERBAL_SIGN_LABELS
export const ASSENT_UNABLE_LABELS = SESSION_RECORDING_ASSENT_UNABLE_LABELS

export const WITHDRAWN_BY_OPTIONS: ReadonlyArray<{ value: SessionRecordingWithdrawnBy; label: string }> = [
  { value: 'participant', label: 'The participant' },
  { value: 'guardian', label: 'Their parent/guardian' },
  { value: 'nominee', label: 'Their nominee' },
  { value: 'other_person_present', label: 'Someone else in the room' },
  { value: 'provider', label: 'Me (the provider)' },
]

export const FLAG_KIND_LABELS: Record<TherapyNoteFlagKind, string> = {
  quote_not_found: 'Quote not found in the transcript',
  no_evidence: 'No evidence in the transcript',
  unsupported: 'Not supported by the transcript',
  overstated: 'May be overstated',
  misattributed: 'May be attributed to the wrong person',
  recommendation_added: 'Recommendation not said in the session',
  off_topic: 'Off topic',
}

const FAILURE_CODE_COPY: Record<string, string> = {
  NO_SPEECH: 'We couldn’t hear any speech in the recording.',
  TRANSCRIBE_FAILED: 'Turning the audio into text didn’t work.',
  BEDROCK_FAILED: 'Drafting the note didn’t work.',
  UPLOAD_NEVER_COMPLETED: 'The recording never finished uploading from the phone.',
}

const FAILURE_STAGE_COPY: Record<SessionRecordingFailure['stage'], string> = {
  upload: 'The upload didn’t finish.',
  transcription: 'Turning the audio into text didn’t work.',
  drafting: 'Drafting the note didn’t work.',
}

/** Plain-English failure explanation (never the raw code). */
export function failureCopy(failure: Pick<SessionRecordingFailure, 'code' | 'stage'>): string {
  return FAILURE_CODE_COPY[failure.code] ?? FAILURE_STAGE_COPY[failure.stage]
}

/** Codes whose server `message` is already the best thing to show. */
const SERVER_MESSAGE_CODES = new Set<string>([
  SESSION_RECORDING_ERROR_CODES.dailyLimit,
  SESSION_RECORDING_ERROR_CODES.alreadyActive,
  SESSION_RECORDING_ERROR_CODES.perSessionLimit,
  SESSION_RECORDING_ERROR_CODES.notInProgress,
  SESSION_RECORDING_ERROR_CODES.notInPerson,
  SESSION_RECORDING_ERROR_CODES.invalidConsent,
  SESSION_RECORDING_ERROR_CODES.regenerateLimit,
  SESSION_RECORDING_ERROR_CODES.retryLimit,
  SESSION_RECORDING_ERROR_CODES.versionConflict,
  SESSION_RECORDING_ERROR_CODES.audioGone,
  SESSION_RECORDING_ERROR_CODES.invalidState,
])

const FALLBACK_CODE_COPY: Record<string, string> = {
  [SESSION_RECORDING_ERROR_CODES.dailyLimit]: 'You’ve reached today’s recording limit.',
  [SESSION_RECORDING_ERROR_CODES.alreadyActive]: 'A recording is already running for this session.',
  [SESSION_RECORDING_ERROR_CODES.perSessionLimit]: 'This session has reached its recording limit.',
  [SESSION_RECORDING_ERROR_CODES.notInProgress]: 'Recording is only available while the session is in progress.',
  [SESSION_RECORDING_ERROR_CODES.notInPerson]: 'Recording is only available for in-person sessions.',
  [SESSION_RECORDING_ERROR_CODES.premiumRequired]: 'Session recording isn’t turned on for your account.',
  [SESSION_RECORDING_ERROR_CODES.forbidden]: 'You don’t have access to this recording.',
  [SESSION_RECORDING_ERROR_CODES.notFound]: 'This recording isn’t available any more.',
  [SESSION_RECORDING_ERROR_CODES.versionConflict]: 'This note was changed somewhere else.',
  [SESSION_RECORDING_ERROR_CODES.regenerateLimit]: 'No regenerations left for this recording.',
  [SESSION_RECORDING_ERROR_CODES.retryLimit]: 'This step has been retried too many times — write the note by hand.',
  [SESSION_RECORDING_ERROR_CODES.audioGone]: 'The audio has already been deleted.',
}

/**
 * User-facing copy for a failed API call: the server's (safe) `message` for
 * the well-known codes, a fixed sentence otherwise. Never shows raw codes.
 */
export function apiErrorCopy(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const { code, message } = err as { code?: unknown; message?: unknown }
    const codeStr = typeof code === 'string' ? code : null
    const hasMessage = typeof message === 'string' && message.length > 0 && !/^Request failed/.test(message)
    if (codeStr && SERVER_MESSAGE_CODES.has(codeStr) && hasMessage) return message as string
    if (codeStr && FALLBACK_CODE_COPY[codeStr]) return FALLBACK_CODE_COPY[codeStr]
    if (hasMessage && (err as { status?: unknown }).status === 0) return message as string
  }
  return fallback
}

/**
 * "Ask in their app" errors: those routes' 4xx messages are written for people
 * ("Wait a minute before asking again."), so show them; a fixed sentence otherwise.
 */
export function consentRequestErrorCopy(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const { status, message } = err as { status?: unknown; message?: unknown }
    const usable = typeof message === 'string' && message.length > 0 && !/^Request failed/.test(message)
    if (usable && typeof status === 'number' && (status === 0 || (status >= 400 && status < 500))) {
      return message as string
    }
  }
  return fallback
}

export function apiErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object') {
    const code = (err as { code?: unknown }).code
    return typeof code === 'string' ? code : null
  }
  return null
}

export function isApiErrorRetryable(err: unknown): boolean {
  if (err && typeof err === 'object' && 'retryable' in err) {
    return (err as { retryable?: unknown }).retryable === true
  }
  // Non-API errors (thrown before the request, e.g. offline fetch) are worth a retry.
  return true
}
